import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Pencil, Trash, Plus, Trash2, Settings, AlertCircle, DollarSign } from 'lucide-react';
import { ServiceAddon } from '../../lib/services';

// 🚨 DEBUG LOGGER FOR EDIT SERVICE MODAL STACKING ISSUE 🚨
const logEditServiceModalDebug = (message: string, data?: any) => {
  console.log(`🚨🔧 [EditServiceModal] ${message}`, data || '');
  
  // Add to debug console if available
  if ((window as any).debugLogger) {
    (window as any).debugLogger.addLog('info', `🚨🔧 [EditServiceModal] ${message}`, data);
  }
};

interface EditServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (service: {
    name: string;
    description: string | null;
    price: number;
    duration_minutes: number;
    is_active: boolean;
    tax_rate?: number;
    tax_exempt?: boolean;
    addons?: ServiceAddon[];
  }) => void;
  service: {
    name: string;
    description: string | null;
    price: number;
    duration_minutes: number;
    is_active: boolean;
    tax_rate?: number;
    tax_exempt?: boolean;
    addons?: ServiceAddon[];
  } | null;
}

interface AddonFormData {
  id?: string;
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
  order_index: number;
}

export default function EditServiceModal({
  isOpen,
  onClose,
  onSave,
  service
}: EditServiceModalProps) {
  const isPaymentsDisabled = false; // No longer using permissions
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingAddonId, setEditingAddonId] = useState<string | null>(null);
  const formInitialized = useRef(false);
  const previousServiceId = useRef<string | null>(null);
  const [addonFormData, setAddonFormData] = useState<AddonFormData>({
    name: '',
    description: '',
    price: 0,
    duration_minutes: 0,
    order_index: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    duration_minutes: 0,
    is_active: true,
    tax_rate: 5,
    tax_exempt: false,
    addons: [] as ServiceAddon[]
  });

  // Update form data when modal opens or service changes
  useEffect(() => {
    // 🚨 DEBUG LOGGING FOR MODAL OPENING 🚨
    if (isOpen) {
      logEditServiceModalDebug('🚀 EDIT SERVICE MODAL OPENING', {
        isOpen,
        hasService: !!service,
        serviceName: service?.name || 'NO SERVICE',
        timestamp: new Date().toISOString(),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        userAgent: navigator.userAgent,
        isPWA: window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true
      });

      // Check DOM state after a brief delay to ensure rendering
      setTimeout(() => {
        const editModalElement = document.querySelector('.edit-service-modal') || 
                                document.querySelector('[class*="edit-service"]') ||
                                document.querySelector('[data-testid="edit-service-modal"]');
        const bookingModalElement = document.querySelector('.booking-info-modal') ||
                                  document.querySelector('[class*="booking-info"]') ||
                                  document.querySelector('[data-testid="booking-info-modal"]');

        logEditServiceModalDebug('🔍 DOM RELATIONSHIP ANALYSIS', {
          editServiceModal: {
            found: !!editModalElement,
            className: editModalElement?.className || 'NOT FOUND',
            zIndex: editModalElement ? window.getComputedStyle(editModalElement).zIndex : 'N/A',
            position: editModalElement ? window.getComputedStyle(editModalElement).position : 'N/A',
            top: editModalElement ? window.getComputedStyle(editModalElement).top : 'N/A',
            transform: editModalElement ? window.getComputedStyle(editModalElement).transform : 'N/A',
            isolation: editModalElement ? window.getComputedStyle(editModalElement).isolation : 'N/A'
          },
          bookingInfoModal: {
            found: !!bookingModalElement,
            className: bookingModalElement?.className || 'NOT FOUND',
            zIndex: bookingModalElement ? window.getComputedStyle(bookingModalElement).zIndex : 'N/A',
            position: bookingModalElement ? window.getComputedStyle(bookingModalElement).position : 'N/A',
            top: bookingModalElement ? window.getComputedStyle(bookingModalElement).top : 'N/A',
            transform: bookingModalElement ? window.getComputedStyle(bookingModalElement).transform : 'N/A',
            isolation: bookingModalElement ? window.getComputedStyle(bookingModalElement).isolation : 'N/A'
          },
          domNesting: {
            editInsideBooking: bookingModalElement && editModalElement ? 
              bookingModalElement.contains(editModalElement) : false,
            bookingInsideEdit: editModalElement && bookingModalElement ? 
              editModalElement.contains(bookingModalElement) : false,
            shareParent: editModalElement && bookingModalElement ? 
              editModalElement.parentElement === bookingModalElement.parentElement : false,
            editParentTag: editModalElement?.parentElement?.tagName || 'UNKNOWN',
            bookingParentTag: bookingModalElement?.parentElement?.tagName || 'UNKNOWN'
          },
          bodyChildren: Array.from(document.body.children).map(child => ({
            tagName: child.tagName,
            className: child.className,
            id: child.id,
            zIndex: window.getComputedStyle(child).zIndex
          })).filter(child => child.className.includes('modal') || child.zIndex !== 'auto')
        });

        // 🚨 CRITICAL: Check if EditServiceModal is nested inside BookingInfoModal 🚨
        if (bookingModalElement && editModalElement && bookingModalElement.contains(editModalElement)) {
          logEditServiceModalDebug('🚨🚨🚨 CRITICAL ISSUE DETECTED: DOM NESTING PROBLEM 🚨🚨🚨', {
            issue: 'EditServiceModal is rendered INSIDE BookingInfoModal DOM tree',
            impact: 'This causes headers and z-index issues regardless of CSS values',
            solution: 'EditServiceModal needs to use React Portal to render at body level',
            recommendation: 'Use createPortal(modalContent, document.body) with standardized z-index (z-9999)',
            urgency: 'HIGH - This is the root cause of the stacking issue'
          });
        }
      }, 50);
    }

    // Only initialize the form when the modal first opens or when the service ID changes
    if (isOpen && service) {
      // Since the service object in the prop type doesn't have an id, we can use any unique identifier
      // We'll use name + duration as a pseudo-identifier or just use 'new' if it's a new service
      const currentServiceId = `${service.name}-${service.duration_minutes}` || 'new';
      const shouldInitializeForm = !formInitialized.current || previousServiceId.current !== currentServiceId;
      
      if (shouldInitializeForm) {
        console.log('[EditServiceModal] Initializing form data with service:', {
          isOpen,
          serviceName: service.name,
          servicePrice: service.price,
          serviceDuration: service.duration_minutes,
          previousId: previousServiceId.current,
          currentId: currentServiceId,
          hasAddons: !!service.addons,
          addonCount: service.addons?.length || 0
        });

        if (service.addons && service.addons.length > 0) {
          // Log each addon to debug issues
          service.addons.forEach((addon, index) => {
            console.log(`[EditServiceModal] Addon ${index+1}:`, {
              id: addon.id || "MISSING ID",
              name: addon.name, 
              price: addon.price,
              addon_service_id: addon.addon_service_id || "MISSING ADDON_SERVICE_ID",
              service_id: addon.service_id || "MISSING SERVICE_ID"
            });
            
            // Fix missing IDs if necessary
            if (!addon.addon_service_id) {
              console.warn(`[EditServiceModal] Fixing missing addon_service_id for addon: ${addon.name}`);
              service.addons![index] = {
                ...addon,
                addon_service_id: addon.id || 'temp-id-' + Math.random().toString(36).substring(2, 9)
              };
            }
          });
        }

        setIsAnimating(true);
        const newFormData = {
          name: service.name,
          description: service.description || '',
          price: service.price,
          duration_minutes: service.duration_minutes,
          is_active: service.is_active,
          tax_rate: service.tax_rate || 5,
          tax_exempt: service.tax_exempt || false,
          addons: service.addons ? service.addons.map(addon => ({
            ...addon,
            // Ensure all required fields are present
            id: addon.id || 'temp-id-' + Math.random().toString(36).substring(2, 9),
            addon_service_id: addon.addon_service_id || addon.id || 'temp-id-' + Math.random().toString(36).substring(2, 9),
            service_id: addon.service_id || '',
            name: addon.name || '',
            description: addon.description || '',
            price: addon.price || 0,
            duration_minutes: addon.duration_minutes || 0,
            order_index: addon.order_index || 0,
            created_at: addon.created_at || new Date().toISOString(),
            updated_at: addon.updated_at || new Date().toISOString()
          })) : []
        };

        console.log('[EditServiceModal] Setting new form data:', {
          name: newFormData.name,
          price: newFormData.price,
          addonCount: newFormData.addons.length
        });
        
        setFormData(newFormData);
        formInitialized.current = true;
        previousServiceId.current = currentServiceId;
      }
    }
  }, [isOpen, service]);

  // Reset the form initialization flag when the modal closes
  useEffect(() => {
    if (!isOpen) {
      formInitialized.current = false;
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      console.log('[EditServiceModal] Modal closing, resetting state');
      setIsAnimating(false);
      setError(null);
      setEditingAddonId(null);
    }
  };

  const handleEditAddon = (addon: ServiceAddon) => {
    setEditingAddonId(addon.id);
    setAddonFormData({
      id: addon.id,
      name: addon.name,
      description: addon.description || '',
      price: addon.price,
      duration_minutes: addon.duration_minutes,
      order_index: addon.order_index
    });
  };

  const handleSaveAddon = () => {
    if (!addonFormData.name.trim()) {
      setError('Add-on name is required');
      return;
    }

    if (addonFormData.price < 0) {
      setError('Add-on price cannot be negative');
      return;
    }

    if (addonFormData.duration_minutes < 0) {
      setError('Add-on duration cannot be negative');
      return;
    }

    setFormData(prev => {
      const updatedAddons = [...prev.addons];
      if (editingAddonId) {
        // Update existing addon
        const index = updatedAddons.findIndex(a => a.id === editingAddonId);
        if (index !== -1) {
          updatedAddons[index] = {
            ...updatedAddons[index],
            ...addonFormData,
            id: editingAddonId
          };
        }
      }
      return { ...prev, addons: updatedAddons };
    });

    setEditingAddonId(null);
    setAddonFormData({
      name: '',
      description: '',
      price: 0,
      duration_minutes: 0,
      order_index: 0
    });
    setError(null);
  };

  const handleDeleteAddon = (addonId: string) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter(a => a.id !== addonId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      console.log('[EditServiceModal] Starting form submission:', {
        formData,
        isLoading: loading,
        hasError: !!error,
        addonCount: formData.addons?.length || 0
      });

      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Service name is required');
      }

      if (formData.price < 0) {
        throw new Error('Price cannot be negative');
      }

      if (formData.duration_minutes < 0) {
        throw new Error('Duration cannot be negative');
      }

      if (!formData.tax_exempt && (formData.tax_rate < 0 || formData.tax_rate > 100)) {
        throw new Error('Tax rate must be between 0 and 100');
      }

      // Check addon data for completeness
      if (formData.addons && formData.addons.length > 0) {
        // Ensure each addon has the required fields
        formData.addons.forEach((addon, index) => {
          if (!addon.id) {
            console.warn(`[EditServiceModal] Addon at index ${index} is missing id:`, addon);
          }
          
          if (!addon.addon_service_id) {
            console.warn(`[EditServiceModal] Addon at index ${index} is missing addon_service_id:`, addon);
            // Try to fix it by using the id
            formData.addons[index] = {
              ...addon,
              addon_service_id: addon.id || ''
            };
          }
        });
        
        console.log('[EditServiceModal] Validated addons:', formData.addons);
      }

      // Save the form data
      const updatedService = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: formData.price,
        duration_minutes: formData.duration_minutes,
        is_active: formData.is_active,
        tax_rate: formData.tax_rate,
        tax_exempt: formData.tax_exempt,
        addons: formData.addons
      };

      console.log('[EditServiceModal] Calling onSave with:', updatedService);
      await onSave(updatedService);

      console.log('[EditServiceModal] Save successful, closing modal');
      onClose();
    } catch (err) {
      console.error('[EditServiceModal] Error in form submission:', err);
      setError(err instanceof Error ? err.message : 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const renderAddonForm = () => (
    <div className="backdrop-blur-sm bg-white/80 rounded-xl p-4 space-y-4 ring-2 ring-purple-500/50 shadow-lg"
         style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-2">
          Add-on Name
        </label>
        <input
          type="text"
          value={addonFormData.name}
          onChange={(e) => setAddonFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 text-sm bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm"
          style={{ 
            minHeight: '36px',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
        />
      </div>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={addonFormData.description}
          onChange={(e) => setAddonFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
          className="w-full px-3 py-2 text-sm bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm resize-none"
          style={{ 
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)'
          }}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!isPaymentsDisabled && (
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-2">
              Price
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-4 w-4 text-purple-500" />
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={addonFormData.price}
                onChange={(e) => setAddonFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                className="w-full pl-10 pr-3 py-2 text-sm bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm"
                style={{ 
                  minHeight: '36px',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)'
                }}
              />
            </div>
          </div>
        )}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-2">
            Duration (hours)
          </label>
          <div className="relative">
            <input
              type="number"
              min="0"
              step="0.5"
              value={addonFormData.duration_minutes / 60}
              onChange={(e) => setAddonFormData(prev => ({ 
                ...prev, 
                duration_minutes: Math.round(parseFloat(e.target.value) * 60) 
              }))}
              className="w-full pl-3 pr-10 py-2 text-sm bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm"
              style={{ 
                minHeight: '36px',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Clock className="h-4 w-4 text-purple-500" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-2 pt-3">
        <button
          type="button"
          onClick={() => {
            setEditingAddonId(null);
            setError(null);
          }}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-xl hover:bg-gray-50/80 transition-all duration-200 shadow-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveAddon}
          className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm"
        >
          Save Changes
        </button>
      </div>
    </div>
  );

  // 🚨 AGGRESSIVE DEBUGGING FOR PWA MODAL ISSUE 🚨
  if (!isOpen && !isAnimating) {
    logEditServiceModalDebug('❌ MODAL NOT RENDERING - isOpen=false, isAnimating=false');
    return null;
  }

  logEditServiceModalDebug('✅ MODAL SHOULD RENDER', {
    isOpen,
    isAnimating,
    hasService: !!service,
    timestamp: new Date().toISOString()
  });

  // 🚨 SOLUTION: Use React Portal to render modal outside BookingInfoModal DOM tree
  const modalContent = (
    <div 
      className={`edit-service-modal fixed inset-0 bg-black z-[999999] ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={onClose}
      style={{
        // Ensure modal appears above BookingInfoModal in PWA mode
        zIndex: 999999,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        // Force visibility in PWA
        display: isOpen ? 'block' : 'none',
        opacity: isOpen ? 1 : 0
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={e => e.stopPropagation()}
        style={{
          // Force hardware acceleration and ensure proper stacking above BookingInfoModal
          zIndex: 1000000,
          transform: isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(100%, 0, 0)',
          WebkitTransform: isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(100%, 0, 0)',
          WebkitBackfaceVisibility: 'hidden',
          isolation: 'isolate'
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Edit Service
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
          >
            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Service Name Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Description Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Price and Duration Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {!isPaymentsDisabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <DollarSign className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          required
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (hours) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.5"
                        value={formData.duration_minutes / 60}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          duration_minutes: Math.round(parseFloat(e.target.value) * 60) 
                        }))}
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <Clock className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Section - Hidden */}
              {false && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-4">Service Settings</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="tax_exempt"
                      checked={formData.tax_exempt}
                      onChange={(e) => setFormData(prev => ({ ...prev, tax_exempt: e.target.checked }))}
                      className="h-5 w-5 rounded-lg border-purple-300 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <label htmlFor="tax_exempt" className="text-base font-medium text-gray-700 cursor-pointer">
                      Tax exempt
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-5 w-5 rounded-lg border-purple-300 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <label htmlFor="is_active" className="text-base font-medium text-gray-700 cursor-pointer">
                      Active
                    </label>
                  </div>

                  {!formData.tax_exempt && !isPaymentsDisabled && (
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Tax Rate (%)
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.tax_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        className="w-32 px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                        style={{ 
                          minHeight: '44px',
                          fontSize: '16px',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Add-ons Section */}
              {formData.addons && formData.addons.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-4">Add-ons</h3>
                  <div className="space-y-3">
                    {/* Add/Edit Add-on Form */}
                    {editingAddonId && renderAddonForm()}

                    {/* Add-ons List */}
                    {formData.addons.map((addon) => (
                      <div 
                        key={addon.id} 
                        className={`backdrop-blur-sm bg-white/60 rounded-xl p-3 shadow-sm ${
                          editingAddonId === addon.id ? 'ring-2 ring-purple-500/50' : ''
                        }`}
                        style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                      >
                        {editingAddonId !== addon.id && (
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-gray-900">{addon.name}</div>
                              {addon.description && (
                                <div className="text-xs text-gray-600 mt-1">{addon.description}</div>
                              )}
                              <div className="flex items-center text-xs text-purple-600 mt-2">
                                <Clock className="h-3 w-3 mr-1" />
                                {addon.duration_minutes >= 1440 
                                  ? `${Math.round(addon.duration_minutes / 1440)} days`
                                  : `${Math.round(addon.duration_minutes / 60)} hours`
                                }
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-3">
                              {!isPaymentsDisabled && (
                                <div className="text-sm font-bold text-purple-700">
                                  ${addon.price.toLocaleString()}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => handleEditAddon(addon)}
                                className="p-2 text-purple-600 hover:text-purple-700 rounded-lg hover:bg-purple-100/50 transition-all duration-200"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAddon(addon.id)}
                                className="p-2 text-red-500 hover:text-red-600 rounded-lg hover:bg-red-100/50 transition-all duration-200"
                              >
                                <Trash className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="p-6 bg-white border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-sm ${
                  loading ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving Changes...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  logEditServiceModalDebug('✅ USING REACT PORTAL TO RENDER OUTSIDE DOM TREE WITH STANDARDIZED Z-INDEX');
  
  // Check if document.body exists (should always be true, but safety check)
  if (!document.body) {
    logEditServiceModalDebug('❌ CRITICAL ERROR: document.body is null!');
    return null;
  }
  
  logEditServiceModalDebug('✅ PORTAL TARGET EXISTS - rendering modal');
  
  // Note: Removed emergency fallback modal - fixed the real issue with CSS z-index
  
  return createPortal(modalContent, document.body);
}